import { PlatformLocation } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import * as moment from 'moment';
import { flatMap, tap } from 'rxjs/operators';
import { getCurrentDateToString, getServerDate, isStartAfterEnd, setServerDate } from '../../shared/helpers/time-helpers.model';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { CustomerIdentification } from '../models/customer-identification.model';
import { CustomerCategoryType } from '../models/enums/customer-category-type.enum';
import { CustomerIdentificationType } from '../models/enums/customer-identification-type.enum';
import { CustomerManageService } from '../shared/customer-manage.service';
import { CustomerModalBaseComponent } from '../shared/customer-modal-base.component';

export class IdentificationContext {
    constructor(public identificationId: number, public customerNumberId: number, public hasOIN: boolean = false) {}
}

@Component({
    templateUrl: './customer-identification-modal.html'
})
export class CustomerIdentificationModalComponent extends CustomerModalBaseComponent {
    public customerIdentificationTypes: any[];
    public stateCodes: any[];
    public countryCodes: any[];
    public showButtonLoading = false;
    public disableDropdowns = false;
    public showState = false;
    public showCountry = false;
    public showOINButton = false;
    public identificationForm: UntypedFormGroup;
    public showValidationErrors = false;
    public loading = true;
    public identificationTypes = new CustomerIdentificationType();
    public effectiveDate: any;
    public endDate: any;
    public mask: string = null;

    private customerCategoryTypes = new CustomerCategoryType();
    public yearRange: string;

    constructor(
        public location: PlatformLocation,
        public fb: UntypedFormBuilder,
        public service: CustomerManageService,
        public dialogRef: MatDialogRef<CustomerIdentificationModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: IdentificationContext,
        public modalService: MvdModalService) {
        super(modalService);
        this.yearRange = `1901:${moment().year() + 30}`;

        location.onPopState(() => {
            this.dialogRef.close();
        });
    }

    public async ngOnInit() {
        this.effectiveDate = moment().toDate();

        this.identificationForm = this.fb.group({
            customerIdentificationTypeId: [''],
            customerIdentificationValue: ['', Validators.required],
            locationCode: [''],
            effectiveDate: [this.effectiveDate],
            endDate: [null]
        }, {
            validator: Validators.compose([this.startDateRequiredValidator, this.startDateValidator, this.endDateRequiredValidator])
        });

        this.identificationForm.get('effectiveDate').valueChanges
            .subscribe(date => this.effectiveDate = setServerDate(date));
        this.identificationForm.get('endDate').valueChanges
            .subscribe(date => this.endDate = setServerDate(date));

        this.service.getIdentificationLookups().subscribe(r => {
            this.stateCodes = r.jurisdictions;
            this.countryCodes = r.countries;
            this.getCustomerType(r.customerIdentificationTypes);
        });

        if (this.data.identificationId) {
            this.initIdentification(this.data.identificationId);
        }
    }

    private getCustomerType(idents: any[]) {
        this.service.getCustomerType(this.data.customerNumberId).subscribe(
            result => {
                this.customerIdentificationTypes = this.filterIdentifications(result, idents);
                this.loading = false;
            },
            e => {
                this.handleValidationResult(e);
                this.loading = false;
            }
        );
    }

    private initIdentification(identificationId: number) {
        this.service.getCustomerIdentification(identificationId).subscribe(
            result => {
                if (result) {
                    this.prepareFormUI(result.customerIdentificationTypeId);
                    this.identificationForm.get('customerIdentificationTypeId').setValue(result.customerIdentificationTypeId);
                    this.identificationForm.get('customerIdentificationTypeId').disable();

                    if (result.customerIdentificationTypeId != this.identificationTypes.SSN.id) {
                        this.identificationForm.get('customerIdentificationValue').setValue(result.customerIdentificationValue);
                    }

                    this.identificationForm.get('locationCode').setValue(result.locationCode);
                    const effectiveDate = getServerDate(result, 'effectiveDate');
                    this.identificationForm.get('effectiveDate').setValue(effectiveDate);
                    const endDate = getServerDate(result, 'endDate');
                    this.identificationForm.get('endDate').setValue(endDate);
                }
            },
            e => this.handleValidationResult(e)
        );
    }

    private filterIdentifications(custType: number, idents: any[]): any[] {
        return idents.filter(ident =>
            (ident.customerCategoryTypeId === custType || ident.customerCategoryTypeId === this.customerCategoryTypes.Both.id) &&
            (ident.id !== this.identificationTypes.OtherIdentificationNumber.id || !this.data.hasOIN || this.data.identificationId) &&
            (ident.id != this.identificationTypes.ArizonaDriverLicense.id) &&
            (ident.id != this.identificationTypes.ArizonaIDNumber.id));
    }

    private prepareFormUI(identificationTypeId: number) {
        this.identificationForm.get('customerIdentificationValue').enable();
        this.showState = false;
        this.showCountry = false;
        this.showOINButton = false;
        this.mask = null;

        switch (identificationTypeId) {
            case this.identificationTypes.OutofStateDriverCredential.id:
            case this.identificationTypes.OutofStateIdentificationCredential.id:
                this.showState = true;
                break;
            case this.identificationTypes.PassportID.id:
            case this.identificationTypes.INSNumber.id:
                this.showCountry = true;
                break;
            case this.identificationTypes.SSN.id:
                this.mask = '999-99-9999';
                break;
            case this.identificationTypes.EIN.id:
                this.mask = '99-9999999';
                break;
            case this.identificationTypes.OtherIdentificationNumber.id:
                this.identificationForm.get('customerIdentificationValue').disable();
                this.showOINButton = !this.data.hasOIN;
                break;
        }
    }

    public clearIdentValue = (): void => {
        this.identificationForm.get('customerIdentificationValue').reset();
        this.identificationForm.get('endDate').reset();
    };

    public cancel() {
        this.dialogRef.close();
    }

    public identificationTypeChange(event: number) {
        this.showValidationErrors = false;
        this.clearIdentValue();
        this.prepareFormUI(event);
        if (event == this.identificationTypes.PassportID.id) {
            this.identificationForm.get('locationCode').setValue('USA');
        } else {
            this.identificationForm.get('locationCode').setValue('');
        }
    }

    public saveIdentification = async (): Promise<void> => {
        this.showValidationErrors = true;

        if (this.identificationForm.valid) {
            this.showButtonLoading = true;
            const identification = new CustomerIdentification();
            identification.id = this.data.identificationId;
            identification.customerNumberId = this.data.customerNumberId;
            identification.customerIdentificationTypeId = this.identificationForm.get('customerIdentificationTypeId').value;
            identification.customerIdentificationValue = this.identificationForm.get('customerIdentificationValue').value;
            identification.locationCode = this.identificationForm.get('locationCode').value;
            identification.effectiveDate = this.identificationForm.get('effectiveDate').value;
            identification.endDate = this.identificationForm.get('endDate').value;

            try {
                await this.service.saveCustomerIdentification(identification).toPromise();
                this.dialogRef.close({ 'ssnChanged': identification.customerIdentificationTypeId == this.identificationTypes.SSN.id });
            } catch (e) {
                this.handleValidationResult(e);
            } finally {
                this.showButtonLoading = false;
            }
        }
    };

    private startDateRequiredValidator(group: UntypedFormGroup) {
        const effDate = group.get('effectiveDate') ? group.get('effectiveDate').value : null;
        if (!effDate) {
            return { 'startDateRequired': true };
        }
    }

    private startDateValidator(group: UntypedFormGroup): { [key: string]: boolean } {
        const effDate = group.get('effectiveDate').value;
        const endDate = group.get('endDate').value;
        if (isStartAfterEnd(effDate, endDate)) {
            return { 'startAfterEndDate': true };
        }
    }

    private endDateRequiredValidator = (group: UntypedFormGroup): any => {
        const endDate = group.get('endDate') ? group.get('endDate').value : null;
        if ((group.get('endDate').value == null || group.get('endDate').value.length == 0) &&
				group.get('customerIdentificationTypeId').value != null &&
				(group.get('customerIdentificationTypeId').value == this.identificationTypes.OutofStateIdentificationCredential.id ||
				group.get('customerIdentificationTypeId').value == this.identificationTypes.OutofStateDriverCredential.id ||
				group.get('customerIdentificationTypeId').value == this.identificationTypes.PassportID.id ||
				group.get('customerIdentificationTypeId').value == this.identificationTypes.INSNumber.id ||
				group.get('customerIdentificationTypeId').value == this.identificationTypes.MilitaryID.id)) {
            return { 'endDateRequired': true };
        }
    };

    public deleteCustomerIdentification() {
        const itemType = this.identificationTypes.FromId(this.identificationForm.get('customerIdentificationTypeId').value).displayValue;

        this.endDateRecordConfirmation(itemType)
            .pipe(
                tap(() => this.identificationForm.get('endDate').setValue(getCurrentDateToString())),
                flatMap(() => this.saveIdentification())
            )
            .subscribe(() => {}, e => console.log(e));
    }

    public generateOIN() {
        this.service.generateOIN().subscribe(
            result => this.identificationForm.get('customerIdentificationValue').setValue(result.oin),
            e => this.handleValidationResult(e)
        );
    }
}
